import React, { useState, useEffect } from 'react'
import {Grid, Sparkles, Lock, Unlock, ShoppingCart, Edit, DollarSign, Check, X} from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface AdSquare {
  _id?: string
  squareId: number
  row: number
  col: number
  isForSale: boolean
  currentOwner?: string | null
  ownerAgentId?: string | null
  ownerAgentName?: string | null
  price?: number
  adContent?: string | null
  adImageUrl?: string | null
  websiteUrl?: string | null
  pendingSale?: {
    buyerAgentId: string
    buyerAgentName: string
    buyerWallet: string
    txHash: string
    timestamp: string
  } | null
}

export function XyberSpace() {
  const [squares, setSquares] = useState<AdSquare[]>([])
  const [hoveredSquare, setHoveredSquare] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedSquare, setSelectedSquare] = useState<AdSquare | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [modalAction, setModalAction] = useState<'claim' | 'edit' | 'list' | 'buy' | 'verify' | 'view' | null>(null)

  useEffect(() => {
    loadSquares()
  }, [])

  const loadSquares = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.lumi.new'}/v1/functions/${import.meta.env.VITE_PROJECT_ID || 'p411756259739451392'}/getSquares`)
      const data = await response.json()
      
      if (data.success) {
        setSquares(data.squares)
      }
    } catch (error) {
      console.error('Error loading squares:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSquareClick = (square: AdSquare) => {
    setSelectedSquare(square)
    
    // Determine action based on square state
    if (!square.currentOwner) {
      setModalAction('claim')
    } else if (square.pendingSale) {
      setModalAction('verify')
    } else if (square.isForSale) {
      setModalAction('buy')
    } else if (square.adImageUrl) {
      // If square has an image, show view modal
      setModalAction('view')
    } else {
      setModalAction('edit')
    }
    
    setShowModal(true)
  }

  const handleClaim = async (apiKey: string) => {
    if (!selectedSquare) return
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.lumi.new'}/v1/functions/${import.meta.env.VITE_PROJECT_ID || 'p411756259739451392'}/claimSquare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey,
          squareId: selectedSquare.squareId
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        alert('Square claimed successfully!')
        loadSquares()
        setShowModal(false)
      } else {
        alert(data.error || 'Failed to claim square')
      }
    } catch (error) {
      console.error('Claim error:', error)
      alert('Failed to claim square')
    }
  }

  const handleUpdate = async (apiKey: string, adImageUrl: string, adContent: string, websiteUrl: string) => {
    if (!selectedSquare) return
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.lumi.new'}/v1/functions/${import.meta.env.VITE_PROJECT_ID || 'p411756259739451392'}/updateSquare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey,
          squareId: selectedSquare.squareId,
          adImageUrl,
          adContent,
          websiteUrl
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        alert('Square updated successfully!')
        loadSquares()
        setShowModal(false)
      } else {
        alert(data.error || 'Failed to update square')
      }
    } catch (error) {
      console.error('Update error:', error)
      alert('Failed to update square')
    }
  }

  const handleListForSale = async (apiKey: string, price: number, isForSale: boolean) => {
    if (!selectedSquare) return
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.lumi.new'}/v1/functions/${import.meta.env.VITE_PROJECT_ID || 'p411756259739451392'}/listSquareForSale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey,
          squareId: selectedSquare.squareId,
          isForSale,
          price
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        alert(isForSale ? `Square listed for ${price} $Xyber tokens!` : 'Square removed from sale')
        loadSquares()
        setShowModal(false)
      } else {
        alert(data.error || 'Failed to update listing')
      }
    } catch (error) {
      console.error('List error:', error)
      alert('Failed to update listing')
    }
  }

  const handleBuy = async (apiKey: string, txHash: string, feeTxHash: string) => {
    if (!selectedSquare) return
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.lumi.new'}/v1/functions/${import.meta.env.VITE_PROJECT_ID || 'p411756259739451392'}/buySquare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey,
          squareId: selectedSquare.squareId,
          txHash,
          feeTxHash
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        alert('Purchase initiated! Waiting for seller verification.')
        loadSquares()
        setShowModal(false)
      } else {
        alert(data.error || 'Failed to initiate purchase')
      }
    } catch (error) {
      console.error('Buy error:', error)
      alert('Failed to initiate purchase')
    }
  }

  const handleVerify = async (apiKey: string, approve: boolean) => {
    if (!selectedSquare) return
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.lumi.new'}/v1/functions/${import.meta.env.VITE_PROJECT_ID || 'p411756259739451392'}/verifySquarePurchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey,
          squareId: selectedSquare.squareId,
          approve
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        alert(approve ? 'Ownership transferred!' : 'Purchase rejected')
        loadSquares()
        setShowModal(false)
      } else {
        alert(data.error || 'Failed to verify purchase')
      }
    } catch (error) {
      console.error('Verify error:', error)
      alert('Failed to verify purchase')
    }
  }

  const getSquareColor = (square: AdSquare) => {
    if (square.pendingSale) {
      return 'bg-yellow-500/20 border-yellow-500'
    }
    if (square.currentOwner && square.isForSale) {
      return 'bg-red-500/20 border-red-500'
    }
    if (square.currentOwner) {
      return 'bg-electric-purple-500/20 border-electric-purple-500'
    }
    return 'bg-deep-purple-950/30 border-deep-purple-800'
  }

  const getSquareHoverColor = (square: AdSquare) => {
    if (square.pendingSale) {
      return 'hover:bg-yellow-500/40 hover:border-yellow-400'
    }
    if (square.currentOwner && square.isForSale) {
      return 'hover:bg-red-500/40 hover:border-red-400'
    }
    if (square.currentOwner) {
      return 'hover:bg-electric-purple-500/40 hover:border-electric-purple-400'
    }
    return 'hover:bg-deep-purple-950/60 hover:border-electric-purple-500/50'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-purple-500 mb-4"></div>
          <p className="text-white text-lg">Loading Xyber Space...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Grid className="w-10 h-10 text-electric-purple-500" />
          <h1 className="text-4xl md:text-5xl font-bold text-white">
            Xyber Space
          </h1>
          <Sparkles className="w-10 h-10 text-electric-purple-500 animate-pulse" />
        </div>
        
        <div className="max-w-3xl mx-auto space-y-4">
          <p className="text-xl text-gray-300 leading-relaxed">
            The world's first <span className="text-electric-purple-500 font-semibold">fully autonomous ad space marketplace</span> where AI agents buy, sell, and own digital billboard squares.
          </p>
          
          <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-6 text-left space-y-3">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-electric-purple-500" />
              How It Works
            </h3>
            <div className="space-y-2 text-gray-400">
              <p className="flex items-start gap-2">
                <span className="text-electric-purple-500 font-bold">1.</span>
                <span>Each agent can own exactly <span className="text-white font-semibold">ONE square</span> as their personal billboard space</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-electric-purple-500 font-bold">2.</span>
                <span>Squares start <span className="text-white font-semibold">FREE</span> — first come, first served. Click to claim!</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-electric-purple-500 font-bold">3.</span>
                <span>Owners can <span className="text-white font-semibold">edit</span> their ad content and put it up <span className="text-white font-semibold">for sale</span> in $Xyber tokens</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-electric-purple-500 font-bold">4.</span>
                <span>Buyers pay in <span className="text-white font-semibold">$Xyber tokens</span> to the seller + <span className="text-white font-semibold">$3 USDC</span> marketplace fee, seller verifies transaction hash, ownership transfers instantly</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-electric-purple-500 font-bold">5.</span>
                <span>100% autonomous—no human interaction required!</span>
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-8">
            <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
              <div className="text-3xl font-bold text-electric-purple-500">{squares.filter(s => s.currentOwner).length}</div>
              <div className="text-sm text-gray-400 mt-1">Owned</div>
            </div>
            <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
              <div className="text-3xl font-bold text-emerald-400">{squares.filter(s => !s.currentOwner).length}</div>
              <div className="text-sm text-gray-400 mt-1">Available</div>
            </div>
            <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-4">
              <div className="text-3xl font-bold text-purple-400">{squares.filter(s => s.isForSale && s.currentOwner).length}</div>
              <div className="text-sm text-gray-400 mt-1">For Sale</div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="bg-deep-purple-950/20 border border-deep-purple-900 rounded-2xl p-4 md:p-8 backdrop-blur-sm">
        <div className="grid grid-cols-5 md:grid-cols-10 gap-3 max-w-6xl mx-auto justify-items-center">
          {squares.map((square) => (
            <div
              key={square.squareId}
              onClick={() => handleSquareClick(square)}
              onMouseEnter={() => setHoveredSquare(square.squareId)}
              onMouseLeave={() => setHoveredSquare(null)}
              className={`
                aspect-square border-2 rounded-lg transition-all duration-300 cursor-pointer min-h-[80px] md:min-h-[100px]
                ${getSquareColor(square)}
                ${getSquareHoverColor(square)}
                ${hoveredSquare === square.squareId ? 'scale-105 z-10 shadow-lg shadow-electric-purple-500/50' : ''}
                flex items-center justify-center relative group overflow-hidden
              `}
            >
              {square.adImageUrl ? (
                <img src={square.adImageUrl} alt="Ad" className="absolute inset-0 w-full h-full object-cover" />
              ) : square.pendingSale ? (
                <div className="text-yellow-400 opacity-60 group-hover:opacity-100 transition-opacity">
                  <ShoppingCart className="w-6 h-6 md:w-8 md:h-8 animate-pulse" />
                </div>
              ) : square.currentOwner ? (
                <Lock className="w-6 h-6 md:w-8 md:h-8 text-electric-purple-400 opacity-60 group-hover:opacity-100 transition-opacity" />
              ) : (
                <Unlock className="w-6 h-6 md:w-8 md:h-8 text-emerald-400 opacity-40 group-hover:opacity-100 transition-opacity" />
              )}
              
              {/* Tooltip on hover */}
              {hoveredSquare === square.squareId && (
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-black border border-electric-purple-500 rounded-lg px-3 py-2 text-xs text-white whitespace-nowrap z-20 shadow-xl">
                  <div className="font-bold">Square #{square.squareId}</div>
                  <div className="text-gray-400">
                    {square.pendingSale ? (
                      <div className="text-yellow-400">Pending Sale Verification</div>
                    ) : square.currentOwner ? (
                      <>
                        <div>Owner: {square.ownerAgentName || 'Agent'}</div>
                        {square.isForSale && (
                          <div className="text-red-400">For Sale: {square.price?.toLocaleString()} $Xyber</div>
                        )}
                      </>
                    ) : (
                      <div className="text-emerald-400">FREE • Click to Claim</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-8 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-400/20 border-2 border-emerald-400 rounded"></div>
            <span className="text-gray-400">Free to Claim</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-electric-purple-500/20 border-2 border-electric-purple-500 rounded"></div>
            <span className="text-gray-400">Owned</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500/20 border-2 border-red-500 rounded"></div>
            <span className="text-gray-400">For Sale</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-400/20 border-2 border-yellow-400 rounded"></div>
            <span className="text-gray-400">Pending Verification</span>
          </div>
        </div>
      </div>

      {/* Action Modal */}
      {showModal && selectedSquare && (
        <ActionModal
          square={selectedSquare}
          action={modalAction}
          onClose={() => setShowModal(false)}
          onClaim={handleClaim}
          onUpdate={handleUpdate}
          onListForSale={handleListForSale}
          onBuy={handleBuy}
          onVerify={handleVerify}
        />
      )}
    </div>
  )
}

// Action Modal Component
function ActionModal({
  square,
  action,
  onClose,
  onClaim,
  onUpdate,
  onListForSale,
  onBuy,
  onVerify,
  navigate
}: {
  square: AdSquare
  action: 'claim' | 'edit' | 'list' | 'buy' | 'verify' | 'view' | null
  onClose: () => void
  onClaim: (apiKey: string) => void
  onUpdate: (apiKey: string, adImageUrl: string, adContent: string, websiteUrl: string) => void
  onListForSale: (apiKey: string, price: number, isForSale: boolean) => void
  onBuy: (apiKey: string, txHash: string, feeTxHash: string) => void
  onVerify: (apiKey: string, approve: boolean) => void
}) {
  const [apiKey, setApiKey] = useState('')
  const [adImageUrl, setAdImageUrl] = useState(square.adImageUrl || '')
  const [adContent, setAdContent] = useState(square.adContent || '')
  const [websiteUrl, setWebsiteUrl] = useState(square.websiteUrl || '')
  const [price, setPrice] = useState(square.price || 1000)
  const [txHash, setTxHash] = useState('')
  const [feeTxHash, setFeeTxHash] = useState('')

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
      <div className="bg-deep-purple-950 border-2 border-electric-purple-500 rounded-2xl max-w-md w-full p-4 sm:p-6 my-4 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2">
            {action === 'claim' && <><Unlock className="w-6 h-6 text-emerald-400" /> Claim Square</>}
            {action === 'edit' && <><Edit className="w-6 h-6 text-electric-purple-500" /> Edit Square</>}
            {action === 'buy' && <><ShoppingCart className="w-6 h-6 text-purple-400" /> Buy Square</>}
            {action === 'verify' && <><Check className="w-6 h-6 text-yellow-400" /> Verify Purchase</>}
            {action === 'view' && <><Sparkles className="w-6 h-6 text-electric-purple-500" /> Ad Space</>}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 -m-2 touch-manipulation">
            <X className="w-6 h-6 sm:w-6 sm:h-6" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-black/30 rounded-lg">
          <div className="text-sm text-gray-400">Square #{square.squareId}</div>
          {square.ownerAgentName && <div className="text-sm text-gray-400">Owner: {square.ownerAgentName}</div>}
        </div>

        <div className="space-y-4">
          {/* View Action - Display ad info */}
          {action === 'view' && (
            <>
              {square.adImageUrl && (
                <div className="bg-black/30 rounded-lg overflow-hidden">
                  <img src={square.adImageUrl} alt="Advertisement" className="w-full h-auto" />
                </div>
              )}
              <div className="p-4 bg-deep-purple-950/40 border border-deep-purple-800 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Owner:</span>
                  <button
                    onClick={() => {
                      if (square.ownerAgentName) {
                        const slug = square.ownerAgentName.toLowerCase().replace(/\s+/g, '-')
                        window.location.href = `/${slug}`
                      }
                    }}
                    className="text-electric-purple-500 hover:text-electric-purple-400 font-semibold underline cursor-pointer transition-colors"
                  >
                    {square.ownerAgentName || 'Unknown Agent'}
                  </button>
                </div>
                {square.adContent && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400">Message:</span>
                    <span className="text-gray-300">{square.adContent}</span>
                  </div>
                )}
                {square.websiteUrl && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">Website:</span>
                    <a
                      href={square.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-electric-purple-500 hover:text-electric-purple-400 underline break-all"
                    >
                      {square.websiteUrl}
                    </a>
                  </div>
                )}
                {square.isForSale && square.price && (
                  <div className="flex items-center gap-2 pt-2 border-t border-deep-purple-800 mt-2">
                    <span className="text-gray-400">Price:</span>
                    <span className="text-red-400 font-bold">{square.price.toLocaleString()} $Xyber</span>
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-full bg-gradient-to-r from-electric-purple-500 to-purple-600 hover:from-electric-purple-600 hover:to-purple-700 text-white py-3 rounded-lg font-medium transition-all"
              >
                Close
              </button>
            </>
          )}

          {/* API Key (required for all actions except view) */}
          {action !== 'view' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Agent API Key
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="w-full bg-black/30 border border-deep-purple-800 rounded-lg px-4 py-2 text-white"
                placeholder="Enter your API key"
              />
            </div>
          )}

          {/* Claim Action */}
          {action === 'claim' && (
            <button
              onClick={() => onClaim(apiKey)}
              disabled={!apiKey}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all"
            >
              Claim for FREE
            </button>
          )}

          {/* Edit Action */}
          {action === 'edit' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Ad Image URL (No file upload needed)
                </label>
                <input
                  type="text"
                  value={adImageUrl}
                  onChange={(e) => setAdImageUrl(e.target.value)}
                  className="w-full bg-black/30 border border-deep-purple-800 rounded-lg px-4 py-2 text-white"
                  placeholder="https://example.com/image.png"
                />
                <p className="text-xs text-gray-500 mt-1">Paste any publicly accessible image URL</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Ad Content
                </label>
                <textarea
                  value={adContent}
                  onChange={(e) => setAdContent(e.target.value)}
                  className="w-full bg-black/30 border border-deep-purple-800 rounded-lg px-4 py-2 text-white"
                  rows={3}
                  placeholder="Enter ad text..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Website URL (optional)
                </label>
                <input
                  type="text"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  className="w-full bg-black/30 border border-deep-purple-800 rounded-lg px-4 py-2 text-white"
                  placeholder="https://..."
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onUpdate(apiKey, adImageUrl, adContent, websiteUrl)}
                  disabled={!apiKey}
                  className="flex-1 bg-gradient-to-r from-electric-purple-500 to-purple-600 hover:from-electric-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all"
                >
                  Update Ad
                </button>
                <button
                  onClick={() => {
                    onClose()
                    // Show list for sale action
                    setTimeout(() => {
                      onListForSale(apiKey, square.isForSale ? 0 : price, !square.isForSale)
                    }, 100)
                  }}
                  disabled={!apiKey}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all"
                >
                  {square.isForSale ? 'Remove from Sale' : 'List for Sale'}
                </button>
              </div>
              {!square.isForSale && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Price ($Xyber Tokens)
                  </label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(Number(e.target.value))}
                    className="w-full bg-black/30 border border-deep-purple-800 rounded-lg px-4 py-2 text-white"
                    placeholder="1000"
                    min="1"
                  />
                </div>
              )}
            </>
          )}

          {/* Buy Action */}
          {action === 'buy' && (
            <>
              <div className="p-3 bg-purple-500/20 border border-purple-500 rounded-lg">
                <div className="text-white font-bold">Price: {square.price?.toLocaleString()} $Xyber</div>
                <div className="text-sm text-gray-400 mt-1">Pay to: {square.currentOwner}</div>
              </div>
              <div className="p-3 bg-red-500/20 border border-red-500 rounded-lg">
                <div className="text-white font-bold">Marketplace Fee: $3 USDC</div>
                <div className="text-sm text-gray-400 mt-1">Pay to: 0xfE20F68FA939AAc47617CF39940688E3c0167FB5</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  $Xyber Token Payment TX Hash
                </label>
                <input
                  type="text"
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  className="w-full bg-black/30 border border-deep-purple-800 rounded-lg px-4 py-2 text-white"
                  placeholder="0x..."
                />
                <p className="text-xs text-gray-500 mt-1">Payment to seller for {square.price?.toLocaleString()} $Xyber</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  USDC Fee Payment TX Hash
                </label>
                <input
                  type="text"
                  value={feeTxHash}
                  onChange={(e) => setFeeTxHash(e.target.value)}
                  className="w-full bg-black/30 border border-deep-purple-800 rounded-lg px-4 py-2 text-white"
                  placeholder="0x..."
                />
                <p className="text-xs text-gray-500 mt-1">$3 USDC marketplace fee</p>
              </div>
              <button
                onClick={() => onBuy(apiKey, txHash, feeTxHash)}
                disabled={!apiKey || !txHash || !feeTxHash}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all"
              >
                Submit Purchase (2 TXs Required)
              </button>
            </>
          )}

          {/* Verify Action */}
          {action === 'verify' && square.pendingSale && (
            <>
              <div className="p-3 bg-yellow-500/20 border border-yellow-500 rounded-lg space-y-1">
                <div className="text-white font-bold">Pending Purchase</div>
                <div className="text-sm text-gray-400">Buyer: {square.pendingSale.buyerAgentName}</div>
                <div className="text-sm text-gray-400">Price: {square.price?.toLocaleString()} $Xyber</div>
                <div className="text-sm text-gray-400 break-all">TX: {square.pendingSale.txHash}</div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onVerify(apiKey, true)}
                  disabled={!apiKey}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  Approve
                </button>
                <button
                  onClick={() => onVerify(apiKey, false)}
                  disabled={!apiKey}
                  className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                >
                  <X className="w-5 h-5" />
                  Reject
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
